/**
 * FilterConditionsTooltip — renders applied filters as their OR/AND group
 * structure, for use as the CRM filter button's hover tooltip.
 *
 * Structure: OR between groups, AND within a group. A single group renders as
 * a flat AND list (no box); multiple groups render as bordered clusters
 * separated by an "OR" label. This is the only place the filter logic is shown
 * on the list view now that the separate chips bar is gone, so it must make the
 * grouping legible at a glance.
 *
 * Presentational only — it takes already-normalized groups (valid, non-empty
 * conditions) and the label/schema maps needed to format each condition. The
 * caller (CrmListLayout) owns the normalization and the badge count.
 */

import { Fragment, useMemo } from 'react';

import { EntityBadgeById } from '@/lib/crm/components/badges';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type {
  FilterCondition,
  FilterConditionGroup,
  OperatorType,
} from '@/lib/crm/filters/types';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getFilterConditionDisplayLabel } from '@/lib/crm/list/components/filterChipLabels';
import { cx } from '@/utils/cx';
import { splitCommaSeparatedValue } from '@/utils/filterUtils';

const OPERATOR_LABELS: Record<OperatorType, string> = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  contains_any: 'contains any of',
  not_contains_any: 'does not contain any of',
  starts_with: 'starts with',
  ends_with: 'ends with',
  greater_than: '>',
  less_than: '<',
  greater_than_or_equal: '≥',
  less_than_or_equal: '≤',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  in: 'is one of',
  not_in: 'is not one of',
};

/**
 * Normalize a multi-select condition's value to an array before display.
 *
 * The interactive filter panel stores multi-select `in`/`not_in` values as a
 * comma-joined STRING (`PropertyValueInput` joins selections on ','), while
 * saved views and programmatic filters carry real arrays. `filterUtils`
 * performs this exact split at the query boundary; mirroring it here means
 * both encodings take `PropertyValueLabel`'s multi-value branch — full
 * expansion with per-element option-label resolution — instead of the string
 * shape falling through to a raw comma-joined render. The split matches what
 * the query actually executes, so the tooltip always shows the effective
 * filter values.
 */
function normalizeMultiValueConditionValue(
  condition: FilterCondition,
  schema: PropertyTypeSchema | undefined
): FilterCondition['value'] {
  if (
    schema?.is_multi &&
    typeof condition.value === 'string' &&
    (condition.operator === 'in' || condition.operator === 'not_in')
  ) {
    return splitCommaSeparatedValue(condition.value);
  }
  return condition.value;
}

function FilterConditionPropertyValue({
  condition,
  schema,
}: {
  condition: FilterCondition;
  schema: PropertyTypeSchema | undefined;
}) {
  const normalizedValue = useMemo(
    () => normalizeMultiValueConditionValue(condition, schema),
    [condition, schema]
  );
  return (
    <PropertyValueLabel
      value={normalizedValue}
      propertyType={schema}
      fontSize='xs'
      expandAllValues
    />
  );
}

export interface FilterConditionsTooltipProps {
  /**
   * OR-groups of AND-conditions, already filtered to valid conditions and with
   * empty groups removed. Rendering assumes each group has ≥1 condition.
   */
  groups: FilterConditionGroup[];
  /** Property name → display label */
  propertyLabels?: Record<string, string>;
  /** Property name → schema, for value formatting */
  propertySchemas?: Record<string, PropertyTypeSchema>;
  /** Association type name → display label */
  associationLabels?: Record<string, string>;
  /** Org id — used to hydrate association badges */
  orgId: number;
}

export function FilterConditionsTooltip({
  groups,
  propertyLabels = {},
  propertySchemas = {},
  associationLabels = {},
  orgId,
}: FilterConditionsTooltipProps) {
  const isMultiGroup = groups.length > 1;

  return (
    <span className='dark-mode flex flex-col gap-2 text-left'>
      {groups.map((group, groupIndex) => (
        <Fragment key={group.id}>
          {groupIndex > 0 && (
            <span className='text-[10px] font-semibold uppercase tracking-wider text-fg-quaternary'>
              OR
            </span>
          )}
          <span
            className={cx(
              'flex flex-col gap-1',
              isMultiGroup && 'rounded-md border border-primary px-2 py-1.5'
            )}
          >
            {group.conditions.map((condition, conditionIndex) => {
              // Associated-property conditions (target_property set) render like
              // a property — the value is a property value, not an entity id, so
              // it must NOT go through EntityBadgeById (which would 404 →
              // "Unavailable").
              const isAssociatedProperty = !!condition.target_property;
              const isAssociation =
                !!condition.association && !isAssociatedProperty;
              const label = getFilterConditionDisplayLabel(
                condition,
                associationLabels,
                propertyLabels
              );
              const operator =
                OPERATOR_LABELS[condition.operator] || condition.operator;
              const needsValue = !['is_empty', 'is_not_empty'].includes(
                condition.operator
              );

              return (
                <span
                  key={condition.id}
                  className='flex items-baseline gap-1 text-xs'
                >
                  {conditionIndex > 0 && (
                    <span className='text-[10px] font-semibold uppercase text-fg-quaternary'>
                      and
                    </span>
                  )}
                  <span className='font-semibold'>{label}</span>
                  <span className='opacity-70'>{operator}</span>
                  {needsValue &&
                    condition.value != null &&
                    (isAssociation ? (
                      <span className='inline-flex items-center gap-1'>
                        {(Array.isArray(condition.value)
                          ? condition.value.map(String)
                          : [String(condition.value)]
                        ).map((id, index) => (
                          <span key={id} className='inline-flex items-center'>
                            {index > 0 && (
                              <span className='text-fg-quaternary mr-1'>,</span>
                            )}
                            <EntityBadgeById
                              entityId={id}
                              orgId={orgId}
                              variant='xs'
                              interactive={false}
                            />
                          </span>
                        ))}
                      </span>
                    ) : (
                      <FilterConditionPropertyValue
                        condition={condition}
                        schema={propertySchemas[condition.field]}
                      />
                    ))}
                </span>
              );
            })}
          </span>
        </Fragment>
      ))}
    </span>
  );
}
